import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ResourcesPage() {
  const [selectedGuide, setSelectedGuide] = useState<number | null>(null);

  const guides = [
    {
      id: 1,
      title: "Getting Started with Machine Learning",
      description: "A comprehensive introduction to ML fundamentals, algorithms, and practical implementation strategies for business applications",
      category: "Guide",
      readTime: "12 min read",
      content: `
        <h3 class="text-2xl font-bold mb-4">Introduction to Machine Learning</h3>
        <p class="mb-4">Machine Learning (ML) is transforming how businesses operate, enabling data-driven decisions and automated processes that were previously impossible. This guide will walk you through the fundamentals and help you understand how to implement ML in your organization.</p>
        
        <h4 class="text-xl font-bold mb-3 mt-6">Understanding ML Fundamentals</h4>
        <p class="mb-4">At its core, machine learning is about teaching computers to learn from data without being explicitly programmed. There are three main types:</p>
        <ul class="list-disc pl-6 mb-4 space-y-2">
          <li><strong>Supervised Learning:</strong> Training models with labeled data to make predictions</li>
          <li><strong>Unsupervised Learning:</strong> Finding patterns in unlabeled data</li>
          <li><strong>Reinforcement Learning:</strong> Learning through trial and error with rewards</li>
        </ul>
        
        <h4 class="text-xl font-bold mb-3 mt-6">Key Algorithms to Know</h4>
        <p class="mb-4">Different problems require different approaches. Here are the most common algorithms:</p>
        <ul class="list-disc pl-6 mb-4 space-y-2">
          <li>Linear Regression for continuous predictions</li>
          <li>Decision Trees for classification tasks</li>
          <li>Neural Networks for complex pattern recognition</li>
          <li>K-Means for clustering and segmentation</li>
        </ul>
        
        <h4 class="text-xl font-bold mb-3 mt-6">Implementation Strategy</h4>
        <p class="mb-4">Successfully implementing ML requires a structured approach:</p>
        <ol class="list-decimal pl-6 mb-4 space-y-2">
          <li>Define clear business objectives and success metrics</li>
          <li>Collect and prepare high-quality training data</li>
          <li>Select appropriate algorithms for your use case</li>
          <li>Train and validate models iteratively</li>
          <li>Deploy and monitor performance continuously</li>
        </ol>
        
        <h4 class="text-xl font-bold mb-3 mt-6">Best Practices</h4>
        <ul class="list-disc pl-6 mb-4 space-y-2">
          <li>Start with simple models before moving to complex ones</li>
          <li>Always validate with separate test data</li>
          <li>Monitor for model drift and retrain regularly</li>
          <li>Ensure data quality and consistency</li>
          <li>Document your process and decisions</li>
        </ul>
      `
    },
    {
      id: 2,
      title: "AI Implementation Best Practices",
      description: "Learn proven strategies for successfully deploying AI solutions in enterprise environments with minimal disruption",
      category: "Guide",
      readTime: "15 min read",
      content: `
        <h3 class="text-2xl font-bold mb-4">Enterprise AI Implementation</h3>
        <p class="mb-4">Implementing AI in enterprise environments requires careful planning, stakeholder alignment, and a clear roadmap. This guide covers the essential best practices for successful AI deployment.</p>
        
        <h4 class="text-xl font-bold mb-3 mt-6">Pre-Implementation Planning</h4>
        <p class="mb-4">Before diving into AI implementation, establish a solid foundation:</p>
        <ul class="list-disc pl-6 mb-4 space-y-2">
          <li>Conduct a thorough assessment of your current infrastructure</li>
          <li>Identify high-impact use cases with clear ROI</li>
          <li>Secure executive sponsorship and budget allocation</li>
          <li>Build a cross-functional implementation team</li>
          <li>Define success metrics and KPIs upfront</li>
        </ul>
        
        <h4 class="text-xl font-bold mb-3 mt-6">Data Readiness</h4>
        <p class="mb-4">AI is only as good as the data it learns from. Ensure your data is:</p>
        <ul class="list-disc pl-6 mb-4 space-y-2">
          <li>Accurate and properly labeled</li>
          <li>Representative of real-world scenarios</li>
          <li>Sufficient in volume for training</li>
          <li>Properly governed and compliant</li>
          <li>Accessible and well-documented</li>
        </ul>
        
        <h4 class="text-xl font-bold mb-3 mt-6">Phased Rollout Strategy</h4>
        <p class="mb-4">Minimize risk by implementing in stages:</p>
        <ol class="list-decimal pl-6 mb-4 space-y-2">
          <li><strong>Pilot Phase:</strong> Test with a small user group</li>
          <li><strong>Validation Phase:</strong> Measure results and gather feedback</li>
          <li><strong>Expansion Phase:</strong> Scale to broader user base</li>
          <li><strong>Optimization Phase:</strong> Continuously improve performance</li>
        </ol>
        
        <h4 class="text-xl font-bold mb-3 mt-6">Change Management</h4>
        <p class="mb-4">People are key to successful AI adoption:</p>
        <ul class="list-disc pl-6 mb-4 space-y-2">
          <li>Communicate benefits clearly to all stakeholders</li>
          <li>Provide comprehensive training programs</li>
          <li>Address concerns about job displacement proactively</li>
          <li>Create feedback loops for continuous improvement</li>
          <li>Celebrate early wins to build momentum</li>
        </ul>
        
        <h4 class="text-xl font-bold mb-3 mt-6">Monitoring and Maintenance</h4>
        <p class="mb-4">Post-deployment is just the beginning:</p>
        <ul class="list-disc pl-6 mb-4 space-y-2">
          <li>Set up real-time performance monitoring</li>
          <li>Establish alert systems for anomalies</li>
          <li>Schedule regular model retraining</li>
          <li>Maintain detailed audit logs</li>
          <li>Plan for scalability from day one</li>
        </ul>
      `
    },
    {
      id: 3,
      title: "Data Privacy in AI Systems",
      description: "Essential guidelines for maintaining compliance and protecting user data in AI-powered applications",
      category: "Whitepaper",
      readTime: "20 min read",
      content: `
        <h3 class="text-2xl font-bold mb-4">Data Privacy and AI: A Comprehensive Guide</h3>
        <p class="mb-4">As AI systems become more prevalent, protecting user privacy and maintaining regulatory compliance is paramount. This whitepaper explores the critical intersection of AI and data privacy.</p>
        
        <h4 class="text-xl font-bold mb-3 mt-6">Regulatory Landscape</h4>
        <p class="mb-4">Understanding the legal framework is essential:</p>
        <ul class="list-disc pl-6 mb-4 space-y-2">
          <li><strong>GDPR:</strong> European data protection regulation with global impact</li>
          <li><strong>CCPA:</strong> California Consumer Privacy Act requirements</li>
          <li><strong>HIPAA:</strong> Healthcare data protection standards</li>
          <li><strong>Industry-specific regulations:</strong> Financial, education, and other sectors</li>
        </ul>
        
        <h4 class="text-xl font-bold mb-3 mt-6">Privacy by Design Principles</h4>
        <p class="mb-4">Build privacy into your AI systems from the ground up:</p>
        <ol class="list-decimal pl-6 mb-4 space-y-2">
          <li>Proactive not reactive approach to privacy</li>
          <li>Privacy as the default setting</li>
          <li>Privacy embedded into design</li>
          <li>Full functionality with positive-sum outcomes</li>
          <li>End-to-end security throughout lifecycle</li>
          <li>Visibility and transparency</li>
          <li>Respect for user privacy</li>
        </ol>
        
        <h4 class="text-xl font-bold mb-3 mt-6">Data Minimization</h4>
        <p class="mb-4">Collect only what you need:</p>
        <ul class="list-disc pl-6 mb-4 space-y-2">
          <li>Define clear data collection purposes</li>
          <li>Implement data retention policies</li>
          <li>Use anonymization and pseudonymization</li>
          <li>Regular data audits and cleanup</li>
          <li>Secure data deletion procedures</li>
        </ul>
        
        <h4 class="text-xl font-bold mb-3 mt-6">Technical Safeguards</h4>
        <p class="mb-4">Implement robust security measures:</p>
        <ul class="list-disc pl-6 mb-4 space-y-2">
          <li>End-to-end encryption for data in transit and at rest</li>
          <li>Access controls and authentication</li>
          <li>Regular security audits and penetration testing</li>
          <li>Secure model training environments</li>
          <li>Differential privacy techniques</li>
        </ul>
        
        <h4 class="text-xl font-bold mb-3 mt-6">User Rights and Transparency</h4>
        <p class="mb-4">Empower users with control over their data:</p>
        <ul class="list-disc pl-6 mb-4 space-y-2">
          <li>Clear privacy policies in plain language</li>
          <li>Easy-to-use consent mechanisms</li>
          <li>Data access and portability features</li>
          <li>Right to deletion (right to be forgotten)</li>
          <li>Explainable AI decisions</li>
        </ul>
        
        <h4 class="text-xl font-bold mb-3 mt-6">Incident Response</h4>
        <p class="mb-4">Be prepared for potential breaches:</p>
        <ul class="list-disc pl-6 mb-4 space-y-2">
          <li>Develop comprehensive incident response plan</li>
          <li>Establish breach notification procedures</li>
          <li>Maintain detailed incident logs</li>
          <li>Regular security drills and training</li>
          <li>Third-party vendor assessments</li>
        </ul>
      `
    }
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900 font-['Inter',sans-serif]">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-orange-50 via-white to-red-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="mb-4 text-orange-700 text-xs uppercase tracking-[0.2em] font-semibold">
              Knowledge Hub
            </div>
            <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-6">
              AI Resources & Insights
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Explore our collection of guides, whitepapers, and tools to help you navigate the world of artificial intelligence
            </p>
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {guides.map((guide) => (
              <div key={guide.id} className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-xl transition-all">
                <div className="p-8">
                  <div className="flex items-center justify-between mb-4">
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">
                      {guide.category}
                    </span>
                    <span className="text-gray-500 text-xs">{guide.readTime}</span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {guide.title}
                  </h3>
                  
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">
                    {guide.description}
                  </p>
                  
                  <button 
                    onClick={() => setSelectedGuide(guide.id)}
                    className="px-6 py-3 bg-white text-orange-600 rounded-full font-semibold hover:shadow-xl transition-all cursor-pointer whitespace-nowrap w-fit"
                  >
                    Read the Guide →
                  </button>
                </div>
              </div>
            ))}

            {/* Additional Resource Cards */}
            <div className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-xl transition-all">
              <div className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">
                    Article
                  </span>
                  <span className="text-gray-500 text-xs">8 min read</span>
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  The Future of Predictive Analytics
                </h3>
                
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  Discover emerging trends in predictive analytics and how they're reshaping business intelligence
                </p>
                
                <Link 
                  to="/resources"
                  className="px-6 py-3 bg-white text-orange-600 rounded-full font-semibold hover:shadow-xl transition-all cursor-pointer whitespace-nowrap inline-block"
                >
                  Read Article →
                </Link>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-xl transition-all">
              <div className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">
                    Case Study
                  </span>
                  <span className="text-gray-500 text-xs">10 min read</span>
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Retail AI Transformation
                </h3>
                
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  How a major retailer increased revenue by 34% using AI-powered personalization
                </p>
                
                <Link 
                  to="/case-studies"
                  className="px-6 py-3 bg-white text-orange-600 rounded-full font-semibold hover:shadow-xl transition-all cursor-pointer whitespace-nowrap inline-block"
                >
                  View Case Study →
                </Link>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-xl transition-all">
              <div className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">
                    Tool
                  </span>
                  <span className="text-gray-500 text-xs">Free Download</span>
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  AI Readiness Assessment
                </h3>
                
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  Evaluate your organization's readiness for AI implementation with our comprehensive checklist
                </p>
                
                <Link 
                  to="/contact"
                  className="px-6 py-3 bg-white text-orange-600 rounded-full font-semibold hover:shadow-xl transition-all cursor-pointer whitespace-nowrap inline-block"
                >
                  Download Tool →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6">
            <i className="ri-mail-line text-5xl text-orange-600"></i>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Stay Updated with AI Insights
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Subscribe to our newsletter for the latest AI trends, best practices, and exclusive resources
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input 
              type="email" 
              placeholder="Enter your email"
              className="flex-1 px-6 py-3 rounded-full border-2 border-gray-200 focus:border-orange-600 focus:outline-none text-sm"
            />
            <button className="px-8 py-3 bg-orange-600 text-white rounded-full font-semibold hover:shadow-xl transition-all cursor-pointer whitespace-nowrap">
              Subscribe
            </button>
          </div>
        </div>
      </section>

      {/* Guide Modal */}
      {selectedGuide && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-4xl w-full my-8 relative">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-2xl font-bold text-gray-900">
                {guides.find(g => g.id === selectedGuide)?.title}
              </h2>
              <button 
                onClick={() => setSelectedGuide(null)}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-gray-600"></i>
              </button>
            </div>
            
            <div className="px-8 py-8 max-h-[70vh] overflow-y-auto">
              <div 
                className="prose prose-lg max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ 
                  __html: guides.find(g => g.id === selectedGuide)?.content || '' 
                }}
              />
            </div>
            
            <div className="sticky bottom-0 bg-gray-50 px-8 py-6 border-t border-gray-200 rounded-b-2xl flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <i className="ri-time-line mr-2"></i>
                {guides.find(g => g.id === selectedGuide)?.readTime}
              </div>
              <button 
                onClick={() => setSelectedGuide(null)}
                className="px-6 py-3 bg-orange-600 text-white rounded-full font-semibold hover:shadow-xl transition-all cursor-pointer whitespace-nowrap"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
